import { includes, map } from "lodash";
import { useInjection } from "@linkfluence/core/di";
import { DateMath } from "@linkfluence/models/date";
import { Facet } from "@linkfluence/models/facet";
import { Locale } from "@linkfluence/models/locale";
import { Metric } from "@linkfluence/models/metric";
import { useQuery } from "@tanstack/react-query";
import { natural } from "@tootools/sort";
import {
  ProductRepo,
  ProductRepositoryUID,
} from "@radarly/core/products/ProductRepo";
import {
  SearchRepository,
  SearchRepositoryUID,
} from "@radarly/core/search/repositories/SearchRepository";
import { useCurrentUserLocale } from "@radarly/core/user/use-cases";
import { useCurrentWorkspace } from "@radarly/core/workspace/use-cases";
import { FullFacet } from "@radarly/models/facets";
import { Towns } from "@radarly/models/geo";
import { isReviewFacet } from "@radarly/models/review";
import { SearchFragmentTerm } from "@radarly/models/search";
import { ClauseRepo, ClauseRepositoryUID } from "../repositories/ClauseRepo";

function getFacetValues(
  repositories: {
    search: SearchRepository;
    clause: ClauseRepo;
    product: ProductRepo;
  },
  project: number,
  facet: string,
  values: FullFacet["values"],
  locale: Locale = Locale.English,
  workspaceFocuses: number[] = []
): Promise<FullFacet["values"]> {
  if (includes([Facet.Hashtags, Facet.Mentions, Facet.Entities], facet)) {
    return repositories.search
      .searchFragmentData(project, {
        query: {
          and: [
            {
              date: {
                from: DateMath.Last_30_Days,
                to: DateMath.Now,
              },
            },
          ],
        },
        aggs: {
          [facet]: {
            metrics: [Metric.Doc],
            sort: {
              on: Metric.Doc,
              order: "desc",
            },
            size: 200,
          },
        },
      })
      .then((data: SearchFragmentTerm["data"]) => {
        return map(data.aggs[facet].values, (entry) => {
          return {
            id: entry.key,
            label: entry.key,
          };
        });
      });
  } else if (isReviewFacet(facet as Facet)) {
    return repositories.product.getProducts(project, facet as Facet);
  } else if (facet === Facet.ImageRecognitionLogos) {
    return repositories.clause.getBrandLogos(map(values, "id"));
  } else if (facet === Facet.ImageRecognitionPersonIdentifications) {
    return repositories.clause.getCelebrities(map(values, "id"));
  } else if (facet === Facet.Towns) {
    return repositories.clause
      .getTowns(workspaceFocuses, project, locale)
      .then((towns: Towns) => {
        return natural(
          towns["geo-digging"].map((town) => ({
            id: town.idStr,
            label: town.info.name,
          })),
          "label"
        );
      });
  } else {
    return Promise.resolve(values);
  }
}

export function useFacetValues(project: number, facet: FullFacet) {
  const searchRepository = useInjection<SearchRepository>(SearchRepositoryUID);
  const clauseRepository = useInjection<ClauseRepo>(ClauseRepositoryUID);
  const productRepository = useInjection<ProductRepo>(ProductRepositoryUID);
  const locale = useCurrentUserLocale();
  const workspace = useCurrentWorkspace();
  const workspaceFocuses = map(workspace?.focuses, "id");
  const { data, isLoading, isError } = useQuery(
    [
      "facet/values",
      {
        project,
        facet: facet.id,
        values: facet.values,
        locale,
        workspaceFocuses,
      },
    ],
    () =>
      getFacetValues(
        {
          search: searchRepository,
          clause: clauseRepository,
          product: productRepository,
        },
        project,
        facet.id,
        facet.values,
        locale,
        workspaceFocuses
      )
  );
  return {
    loading: isLoading,
    values: isLoading ? [] : data,
    error: data?.length < 1 || isError,
  };
}
